import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createFrameSource } from '../decode/frameSource';
import {
    activeAngle,
    angleIndexOf,
    angleSourceTime,
    audioAngle,
    audioAngleIndexOf,
    clipAngles,
    clipVolume,
    getLayerByKind,
    insetSoundFrom,
    isClipMuted,
    isImageAssetType,
    overlayPlacement,
    pipParams,
    sortedClips,
} from '../state/timelineModel';

/**
 * Draws the timeline at the playhead, sixty times a second.
 *
 * The loop is the whole design: one rAF reads the playhead, works out which
 * clip is under it, converts that to a position in the source file, and paints.
 * Nothing else writes to the canvas, so "what the stage shows" has exactly one
 * definition and it is derivable from the graph alone.
 *
 * During playback the playhead is advanced from a wall clock rather than from
 * the video element's currentTime. The element is the thing being corrected,
 * not the reference — otherwise a clip that stalls buffering would drag the
 * whole timeline's clock backwards with it.
 */

export function useCompositor({
    graph,
    playhead,
    isPlaying,
    totalDuration,
    assetsByFileName,
    resolveAssetUrl,
    canvasRef,
    onPlayheadChange,
    onPlayingChange,
}) {
    const sourceRef = useRef(null);
    const audioRef = useRef(null);
    const clockRef = useRef(null);
    const frameRef = useRef(null);
    const [urls, setUrls] = useState({});
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    /* The real pixel dimensions of the frame being shown. Asset records often
       carry no Width/Height, and the fallback guess (portrait 1080x1920) renders
       a landscape clip as a narrow vertical sliver. The decoded element knows the
       truth, so it is the source once a frame exists. */
    const [frameSize, setFrameSize] = useState(null);

    if (sourceRef.current === null) sourceRef.current = createFrameSource();

    const videoClips = useMemo(
        () => sortedClips(getLayerByKind(graph, 'video', 'layer 1')),
        [graph]
    );
    const audioClips = useMemo(
        () => sortedClips(getLayerByKind(graph, 'audio', 'layer 2')),
        [graph]
    );
    const overlayClips = useMemo(
        () => sortedClips(getLayerByKind(graph, 'overlay', 'overlay')),
        [graph]
    );
    const mainEnd = useMemo(
        () => videoClips.reduce((end, clip) => Math.max(end, clip.end ?? 0), 0),
        [videoClips]
    );
    /* Each inset's decoded size, by file. The stage needs it to put a drag
       handle exactly over the inset the canvas painted. */
    const [insetSizes, setInsetSizes] = useState({});

    /** An inset that plays alone silences everything else while it is on. */
    const isSoloInset = useCallback(
        inset => insetSoundFrom(inset) === 'inset' && !isClipMuted(inset),
        []
    );
    const soloInsetAtPlayhead = playhead < mainEnd && overlayClips.some(inset => (
        playhead >= inset.start && playhead < inset.end && isSoloInset(inset)
    ));

    const clipAt = useCallback((clips, seconds) => (
        clips.find(clip => seconds >= clip.start && seconds < clip.end) || null
    ), []);

    const activeVideo = clipAt(videoClips, playhead);
    const activeAudio = clipAt(audioClips, playhead);

    /* Signed URLs expire, and resolving one is a network round-trip, so they
       are fetched per filename and kept. Re-running on every graph change is
       cheap because everything already resolved is skipped. */
    useEffect(() => {
        let cancelled = false;
        const needed = [...videoClips, ...audioClips, ...overlayClips]
            // A multicam segment can be switched to any of its angles without
            // touching the graph's file list, and the one carrying the sound is
            // played even while it is off screen — so every angle is resolved,
            // not just the live one.
            .flatMap(clip => [
                String(clip.asset_filename || ''),
                ...clipAngles(clip).map(angle => String(angle.asset_filename || '')),
            ])
            .filter(name => name && !urls[name]);

        if (needed.length === 0) return undefined;

        setLoading(true);
        Promise.all(Array.from(new Set(needed)).map(async (fileName) => {
            try {
                return [fileName, await resolveAssetUrl(fileName)];
            } catch {
                return [fileName, null];
            }
        })).then((pairs) => {
            if (cancelled) return;
            const resolved = Object.fromEntries(pairs.filter(([, url]) => url));
            if (Object.keys(resolved).length > 0) setUrls(prev => ({ ...prev, ...resolved }));
            if (pairs.some(([, url]) => !url)) setError('Some clips could not be loaded for preview.');
            setLoading(false);
        });

        return () => { cancelled = true; };
    }, [videoClips, audioClips, overlayClips, urls, resolveAssetUrl]);

    /* The transport clock. Restarted whenever playback starts or the playhead
       is moved by hand, so a scrub during playback resumes from where it was
       dropped rather than snapping back. */
    useEffect(() => {
        if (!isPlaying) {
            clockRef.current = null;
            return undefined;
        }
        clockRef.current = {
            startedAt: performance.now(),
            startPlayhead: playhead >= totalDuration ? 0 : playhead,
        };
        return undefined;
        // Deliberately not re-running on every playhead tick: this effect sets
        // the origin the ticks are measured from, so depending on them would
        // reset the clock on each frame and time would never advance.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isPlaying]);

    // The draw loop.
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;
        const ctx = canvas.getContext('2d');
        const source = sourceRef.current;

        const paint = () => {
            frameRef.current = requestAnimationFrame(paint);

            let now = playhead;
            if (clockRef.current) {
                const elapsed = (performance.now() - clockRef.current.startedAt) / 1000;
                now = clockRef.current.startPlayhead + elapsed;
                if (now >= totalDuration) {
                    onPlayheadChange(totalDuration);
                    onPlayingChange(false);
                    return;
                }
                onPlayheadChange(now);
            }

            const clip = clipAt(videoClips, now);
            const { width, height } = canvas;
            ctx.clearRect(0, 0, width, height);
            ctx.fillStyle = '#000';
            ctx.fillRect(0, 0, width, height);

            // Insets end with the main sequence, exactly as the render cuts them.
            const liveInsets = now < mainEnd
                ? overlayClips.filter(inset => now >= inset.start && now < inset.end)
                : [];

            const playingKeys = [];
            const mainKey = paintMain(clip, now, width, height, liveInsets.some(isSoloInset));
            if (mainKey) playingKeys.push(mainKey);

            liveInsets.forEach((inset) => {
                const key = paintInset(inset, now, width, height);
                if (key) playingKeys.push(key);
            });

            // Everything not on screen right now goes quiet as well as still.
            if (clockRef.current) source.pauseAllExcept(playingKeys);
        };

        /** The main track's clip at `now`. Returns the key of the handle it
         *  played, or null when nothing was drawn. */
        const paintMain = (clip, now, width, height, silencedByInset) => {
            if (!clip) return null;
            // Whichever camera this segment is cut to, which is not necessarily
            // the file the clip was first made from.
            const live = activeAngle(clip);
            const fileName = String(live?.asset_filename || clip.asset_filename || '');
            const url = urls[fileName];
            if (!url) return null;

            const asset = assetsByFileName.get(fileName);
            // A still has no timeline of its own; it is simply held for as long
            // as the clip lasts, so there is nothing to seek.
            if (isImageAssetType(asset?.FileType)) {
                const image = source.acquireImage(url);
                if (image?.complete && image.naturalWidth) {
                    const scale = Math.min(width / image.naturalWidth, height / image.naturalHeight);
                    const w = image.naturalWidth * scale;
                    const h = image.naturalHeight * scale;
                    ctx.drawImage(image, (width - w) / 2, (height - h) / 2, w, h);
                }
                return null;
            }

            const handle = source.acquire(url);
            if (!handle) return null;
            // A media element that failed to load leaves the canvas black and
            // says nothing. React bails out when the message is unchanged, so
            // setting it from the paint loop costs one render, not sixty a second.
            if (handle.failure) {
                setError(`Could not play this clip: ${handle.failure}`);
                return null;
            }

            /* The clip's in-point is on the master clock, which is the take's,
               not this camera's: an angle that started rolling late reaches the
               same moment further into its own file. */
            const masterTime = clip.sourceIn + (now - clip.start);
            const positionInSource = live ? angleSourceTime(live, masterTime) : masterTime;
            if (clockRef.current) {
                // Sound belongs to what is on screen and to nothing else;
                // pauseAllExcept silences whatever it replaced. What the
                // clip's own level then decides is how loud that is — so the
                // preview goes quiet for a muted clip exactly where the render
                // will.
                // A multicam segment whose sound comes from another camera is
                // silent as a picture: that camera is playing on its own
                // element, and hearing both would be hearing the take twice.
                const picturePlaysSound = !live || angleIndexOf(clip) === audioAngleIndexOf(clip);
                // An inset set to play alone takes the soundtrack for its span.
                handle.setMuted(isClipMuted(clip) || !picturePlaysSound || silencedByInset);
                handle.setVolume(clipVolume(clip));
                handle.play();
                handle.syncTo(positionInSource);
                if (handle.blocked) {
                    setError('The browser blocked sound — press play again to enable it.');
                }
            } else {
                // Scrubbing should be silent: a paused element that stays
                // unmuted chirps on every seek.
                handle.setMuted(true);
                handle.pause();
                handle.seek(positionInSource);
            }
            // Returning the identical object when nothing changed lets React bail
            // out, so this costs one render per resolution change, not one a frame.
            if (handle.width && handle.height) {
                setFrameSize(prev => (
                    prev && prev.width === handle.width && prev.height === handle.height
                        ? prev
                        : { width: handle.width, height: handle.height }
                ));
            }

            handle.drawTo(ctx, width, height);
            return handle.key;
        };

        /** One inset over the frame, on an element of its own so it can sit at
         *  a different point of the same file than the main clip does. */
        const paintInset = (inset, now, width, height) => {
            const fileName = String(inset.asset_filename || '');
            const url = urls[fileName];
            if (!url) return null;
            const handle = source.acquire(url, `inset:${inset.id}`);
            if (!handle) return null;
            if (handle.failure) {
                setError(`Could not play an overlay clip: ${handle.failure}`);
                return null;
            }

            const positionInSource = inset.sourceIn + (now - inset.start);
            if (clockRef.current) {
                // Heard exactly when the render mixes it in: not when the main
                // video's sound was chosen, and not at zero.
                handle.setMuted(insetSoundFrom(inset) === 'main' || isClipMuted(inset));
                handle.setVolume(clipVolume(inset));
                handle.play();
                handle.syncTo(positionInSource);
            } else {
                handle.setMuted(true);
                handle.pause();
                handle.seek(positionInSource);
            }

            if (!handle.width || !handle.height) return handle.key;
            setInsetSizes(prev => (
                prev[fileName]?.width === handle.width && prev[fileName]?.height === handle.height
                    ? prev
                    : { ...prev, [fileName]: { width: handle.width, height: handle.height } }
            ));
            const { x, y, scale } = pipParams(inset);
            const rect = overlayPlacement(width, height, handle.width, handle.height, x, y, scale);
            handle.drawRect(ctx, rect.left, rect.top, rect.width, rect.height);
            return handle.key;
        };

        frameRef.current = requestAnimationFrame(paint);
        return () => {
            if (frameRef.current) cancelAnimationFrame(frameRef.current);
        };
    }, [
        canvasRef, videoClips, overlayClips, mainEnd, urls, assetsByFileName, totalDuration, isSoloInset,
        playhead, clipAt, onPlayheadChange, onPlayingChange,
    ]);

    /**
     * What the standalone audio element plays, and where in its file.
     *
     * Two things need an element of their own. A music bed spans clips and
     * outlives any one of them, so no per-clip `<video>` can carry it. And a
     * multicam segment cut to one camera while the sound comes from another
     * needs that other camera audible while it is off screen.
     *
     * The bed wins when both are present, because that is what the render does:
     * add_audio replaces whatever audio reached it.
     */
    const previewSound = useMemo(() => {
        if (activeAudio) {
            return {
                fileName: String(activeAudio.asset_filename || ''),
                sourceTimeAt: (seconds) => activeAudio.sourceIn + (seconds - activeAudio.start),
                volume: clipVolume(activeAudio),
                muted: isClipMuted(activeAudio),
            };
        }
        // A clip with no angles reports the same index for both, so this is
        // only ever true of a real multicam cut away from its own sound.
        if (!activeVideo || angleIndexOf(activeVideo) === audioAngleIndexOf(activeVideo)) return null;
        const sound = audioAngle(activeVideo);
        if (!sound) return null;
        return {
            fileName: String(sound.asset_filename || ''),
            sourceTimeAt: (seconds) => angleSourceTime(
                sound, activeVideo.sourceIn + (seconds - activeVideo.start)
            ),
            volume: clipVolume(activeVideo),
            muted: isClipMuted(activeVideo),
        };
    }, [activeAudio, activeVideo]);

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        // Muted to zero is silent in the render, so it is silent here too
        // rather than playing under an edit that will not have it. The same
        // goes for the span of an inset set to play alone.
        if (!previewSound || previewSound.muted || soloInsetAtPlayhead) {
            if (!audio.paused) audio.pause();
            return;
        }
        const url = urls[previewSound.fileName];
        if (!url) return;

        audio.volume = Math.max(0, Math.min(1, previewSound.volume));

        if (audio.dataset.src !== url) {
            audio.dataset.src = url;
            audio.src = url;
        }
        const position = previewSound.sourceTimeAt(playhead);
        if (Math.abs((audio.currentTime || 0) - position) > 0.35) {
            try { audio.currentTime = Math.max(0, position); } catch { /* not ready */ }
        }
        if (isPlaying && audio.paused) audio.play().catch(() => {});
        if (!isPlaying && !audio.paused) audio.pause();
    }, [previewSound, soloInsetAtPlayhead, urls, playhead, isPlaying]);

    useEffect(() => {
        const source = sourceRef.current;
        return () => source.releaseAll();
    }, []);

    return {
        audioRef, activeVideo, activeAudio, loading, error, frameSize,
        overlayClips, mainEnd, insetSizes,
    };
}
